import { useState } from 'react'

import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import styles from './StudentAddForm.module.scss'
import { checkEmail, getCheckPasswordSecurity } from 'api/user'
import {
  BottomActionBarWrapper,
  BottomActionBar,
} from 'components/BottomActionBar'
import Button from 'components/Button'
import { Row, RowWrapper } from 'components/Row'
import TextInput from 'components/TextInput'
import Title from 'components/Title'
import { hideLoader } from 'store/actions/drawer.actions'
import { checkUsernameAction } from 'store/actions/student.actions'
import { TYPES_DATA_USER } from 'utils/constants'
import exceptionEmailParser from 'utils/ExceptionEmailParser'
import exceptionUsernameParser from 'utils/ExceptionUsernameParser/ExceptionUsernameParser'

const StudentAddForm = ({ onCancel, onSubmit }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [student, setStudent] = useState({
    email: '',
    name: '',
    password: '',
    surnames: '',
    username: '',
  })
  const [showloader, setShowloader] = useState({
    email: false,
    password: false,
    username: false,
  })
  const [exception, setException] = useState({
    email: null,
    password: null,
    username: null,
  })
  const [usernameIsValid, setUsernameIsValid] = useState(null)
  const [emailIsValid, setEmailIsValid] = useState(null)
  const [passwordIsValid, setPasswordIsValid] = useState(null)

  const handleChange = async ({ value, inputChanged }) => {
    const valueForm = value?.trim()
    switch (inputChanged) {
      case TYPES_DATA_USER.NAME:
        setStudent({ ...student, name: value })
        break
      case TYPES_DATA_USER.SURNAMES:
        setStudent({ ...student, surnames: value })
        break
      case TYPES_DATA_USER.USERNAME:
        if (valueForm !== '') {
          setShowloader({ ...showloader, username: true })
          const response = await dispatch(checkUsernameAction(valueForm))
          if (response.exists || response.code) {
            const messageException = exceptionUsernameParser({
              code: response.exists
                ? 'UsernameAlreadyExistsException'
                : response.code,
            })
            setException({ ...exception, username: messageException })
            setUsernameIsValid(false)
          } else {
            setException({ ...exception, username: null })
            setUsernameIsValid(true)
          }
          setShowloader({ ...showloader, username: false })
        } else {
          setException({ ...exception, username: null })
          setUsernameIsValid(null)
        }
        setStudent({ ...student, username: valueForm })
        break
      case TYPES_DATA_USER.EMAIL:
        if (valueForm !== '') {
          setShowloader({ ...showloader, email: true })
          dispatch(hideLoader(true))
          await checkEmail(valueForm)
            .then(({ exists }) => {
              if (exists) {
                const messageException = exceptionEmailParser({
                  code: 'EmailAlreadyExistsException',
                })
                setException({ ...exception, email: messageException })
                setEmailIsValid(false)
              } else {
                setException({ ...exception, email: null })
                setEmailIsValid(true)
              }
            })
            .catch(
              ({
                response: {
                  data: { code },
                },
              }) => {
                const messageException = exceptionEmailParser({ code })
                setException({ ...exception, email: messageException })
                setEmailIsValid(false)
              }
            )
          setShowloader({ ...showloader, email: false })
        } else {
          setException({ ...exception, email: null })
          setEmailIsValid(null)
        }
        setStudent({ ...student, email: valueForm })
        dispatch(hideLoader(false))
        break

      default:
        if (valueForm !== '') {
          dispatch(hideLoader(true))
          setShowloader({ ...showloader, password: true })
          await getCheckPasswordSecurity(valueForm).then(({ code, secure }) => {
            if (secure) {
              setException({ ...exception, password: null })
              setPasswordIsValid(true)
            } else {
              setException({ ...exception, password: t(code) })
              setPasswordIsValid(false)
            }
          })
          setShowloader({ ...showloader, password: false })
          dispatch(hideLoader(false))
        } else {
          setException({ ...exception, password: null })
          setPasswordIsValid(null)
        }
        setStudent({ ...student, password: valueForm })
        break
    }
  }

  const checkDisabledButton = () => {
    const { name, surnames } = student
    return !(
      !!name &&
      !!surnames &&
      (usernameIsValid || emailIsValid) &&
      passwordIsValid
    )
  }

  const handleSubmit = ({ preventDefault }) => {
    preventDefault()
    onSubmit(student)
  }

  return (
    <BottomActionBarWrapper>
      <section className={styles.main}>
        <div className={styles.container}>
          <Title
            subtitle={t(
              'Indica los datos personales del estudiante e introduce un nombre de usuario y contraseña que sean únicos'
            )}
            title={t('Crear estudiante')}
          />
          <form onSubmit={handleSubmit}>
            <div className={styles.form}>
              <RowWrapper>
                <Row>
                  <TextInput
                    isValid={!!student.name}
                    label={t('Nombre')}
                    name={TYPES_DATA_USER.NAME}
                    onChange={(value) =>
                      handleChange({
                        inputChanged: TYPES_DATA_USER.NAME,
                        value,
                      })
                    }
                    type='text'
                  />
                </Row>
                <Row>
                  <TextInput
                    isValid={!!student.surnames}
                    label={t('Apellidos')}
                    name={TYPES_DATA_USER.SURNAMES}
                    onChange={(value) =>
                      handleChange({
                        inputChanged: TYPES_DATA_USER.SURNAMES,
                        value,
                      })
                    }
                    type='text'
                  />
                </Row>
                <Row>
                  <TextInput
                    invalidMessage={t(exception.username)}
                    isValid={usernameIsValid}
                    label={t('Nombre de usuario')}
                    name={TYPES_DATA_USER.USERNAME}
                    onChange={(value) =>
                      handleChange({
                        inputChanged: TYPES_DATA_USER.USERNAME,
                        value,
                      })
                    }
                    showLoader={showloader.username}
                    type='text'
                  />
                </Row>
                <Row>
                  <TextInput
                    invalidMessage={t(exception.email)}
                    isValid={emailIsValid}
                    label={t('Correo electrónico')}
                    name={TYPES_DATA_USER.EMAIL}
                    onChange={(value) =>
                      handleChange({
                        inputChanged: TYPES_DATA_USER.EMAIL,
                        value,
                      })
                    }
                    showLoader={showloader.email}
                    type='text'
                  />
                </Row>
                <Row>
                  <TextInput
                    invalidMessage={exception.password}
                    isValid={passwordIsValid}
                    label={t('Contraseña')}
                    name={TYPES_DATA_USER.PASSWORD}
                    onChange={(value) =>
                      handleChange({
                        inputChanged: TYPES_DATA_USER.PASSWORD,
                        value,
                      })
                    }
                    showLoader={showloader.password}
                    type='password'
                  />
                </Row>
              </RowWrapper>
            </div>
            <BottomActionBar>
              <Button
                classes={{ second: true }}
                id='closeStudentAddModal'
                onPress={onCancel}
              >
                {t('common.actions.cancel', { keySeparator: '.' })}
              </Button>
              <Button
                disabled={checkDisabledButton()}
                id='confirmStudentAddModal'
                onPress={() => onSubmit(student)}
                type='submit'
              >
                {t('Crear cuenta')}
              </Button>
            </BottomActionBar>
          </form>
        </div>
      </section>
    </BottomActionBarWrapper>
  )
}

StudentAddForm.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export default StudentAddForm
